import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { m } from 'framer-motion';
import { cx } from '@linaria/core';
import camelCase from 'lodash/camelCase.js';
import { Tooltip } from '@livecommerce/design-system';

import { buildRoute } from 'constants/routes.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import {
  disabledNavigationLinkStyles,
  ExpandSubMenuButton,
  navigationLinkStyles,
  NavigationListItemSubtitle,
  NavigationListItemTitle,
  StyledArrow,
  StyledChevron,
  StyledIcon,
  SubMenu,
  SubMenuLink,
  SubMenuWrapper,
} from './styles.js';
import { secondaryTransitionConfig } from 'constants/animationConfig.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import AIItemTooltip from './AIItemTooltip.jsx';
import { useTooltipContext } from 'contexts/TooltipContext.jsx';

import constants from 'constants/tooltips.js';

const AnimatedSubMenuWrapper = m(SubMenuWrapper);

const SUBMENU_HEIGHT = 40;

const MainTooltip = ({
  title,
  isShown,
  disabled,
  route,
  routeParams,
  dataTestId,
  children,
}) => {
  const { selectedProduct, orgId } = useProductDataOutsideRoutes();
  const history = useHistory();
  const { isTooltipVisible } = useTooltipContext();
  const { pathname } = useLocation();

  const [normalTooltip, setNormalTooltip] = useState(true);

  useEffect(() => {
    if (dataTestId === 'AIGroundingsModerationMenuItem' && !pathname.includes('ai')) {
      // If Persist tooltip is not visible, show normal tooltip
      setNormalTooltip(!isTooltipVisible(constants.AI_NAVIGATION));
    } else {
      setNormalTooltip(true);
    }
  }, [isTooltipVisible, pathname, dataTestId]);

  const camelCasedTitle = camelCase(title);

  const handleTooltipClick = () => {
    if (disabled) return;
    trackingService.track({
      eventType: 'tooltip-navigation',
      destination: title,
      url: buildRoute(route, { orgId, selectedProduct, ...routeParams }),
    });
    const builtRoute = buildRoute(route, { orgId, selectedProduct, ...routeParams });
    history.push(builtRoute);
  };

  const TooltipComponent = useMemo(() => {
    return normalTooltip ? Tooltip : AIItemTooltip;
  }, [normalTooltip]);

  const tooltipProps = normalTooltip ? {
    isShown,
    tooltipText: title,
    style: { zIndex: '2147483647' },
  } : {
    onAction: handleTooltipClick,
  };

  return (
    <TooltipComponent id={`${camelCasedTitle}Tooltip`} {...tooltipProps}>
      {children}
    </TooltipComponent>
  );
};

const NavigationListItem = ({
  title,
  icon,
  submenu,
  route,
  routeParams,
  exact = false,
  url,
  disabled,
  setOpenedSubMenu,
  isSubMenuOpen,
  onExpandSubMenuButtonClick,
  collapsed,
  dataTestId,
  ...props
}) => {
  const { selectedProduct, orgId } = useProductDataOutsideRoutes();
  const history = useHistory();

  const showSubmenu = isSubMenuOpen && !collapsed;
  const viewHeight = submenu ? submenu.length * SUBMENU_HEIGHT + 8 * submenu.length : 0;

  const handleNavigationLinkClick = () => {
    if (disabled) return;

    trackingService.track({
      eventType: 'sidebar-navigation',
      destination: title,
      path: buildRoute(route, { orgId, selectedProduct, ...routeParams }),
    });
    setOpenedSubMenu(null);
  };

  const handleButtonClick = () => {
    if (disabled) return;

    const builtRoute = buildRoute(submenu[0].route, { orgId, selectedProduct, ...submenu[0].routeParams });
    setOpenedSubMenu(title);
    onExpandSubMenuButtonClick();
    trackingService.track({
      eventType: 'sidebar-navigation:expand-submenu',
      destination: title,
      path: builtRoute,
    });
    history.push(builtRoute);
  };

  const handleSubmenuLinkClick = (title, route) => {
    if (disabled) return;

    trackingService.track({
      eventType: 'sidebar-navigation:submenu',
      destination: title,
      path: route,
    });
  };

  const camelCasedTitle = camelCase(title);

  return (
    <li key={title} {...props}>
      <MainTooltip
        isShown={collapsed}
        title={title}
        id={`${camelCasedTitle}Tooltip`}
        dataTestId={dataTestId}
        route={route}
        routeParams={routeParams}
      >
        {route && !url && (
          <NavLink
            className={cx(navigationLinkStyles, disabled && disabledNavigationLinkStyles)}
            to={buildRoute(route, { orgId, selectedProduct, ...routeParams })}
            onClick={handleNavigationLinkClick}
            data-test-id={dataTestId}
            exact={exact}
          >
            {icon && <StyledIcon name={icon} className="menu-icon" />}
            <NavigationListItemTitle>{title}</NavigationListItemTitle>
          </NavLink>
        )}
        {url && (
          <a
            className={cx(navigationLinkStyles, disabled && disabledNavigationLinkStyles)}
            href={url}
            onClick={handleNavigationLinkClick}
            data-test-id={dataTestId}
          >
            {icon && <StyledIcon name={icon} className="menu-icon" />}
            <NavigationListItemTitle>{title}</NavigationListItemTitle>
          </a>
        )}
        {submenu && (
          <>
            <ExpandSubMenuButton
              onClick={handleButtonClick}
              active={isSubMenuOpen}
              className={cx(disabled && disabledNavigationLinkStyles)}
            >
              <StyledIcon name={icon} />
              <NavigationListItemTitle>{title}</NavigationListItemTitle>
              <StyledChevron className="submenu-chevron" name="chevronMenu" direction="right" />
            </ExpandSubMenuButton>
            <AnimatedSubMenuWrapper
              initial={{ height: 0, opacity: 0 }}
              animate={{
                height: showSubmenu ? viewHeight : 0,
                opacity: showSubmenu ? 1 : 0,
              }}
              transition={secondaryTransitionConfig}
            >
              <SubMenu>
                {submenu.map(({ title, route, routeParams }) => (
                  <li key={title} data-test-id={`${title}SidebarSubMenuItem`}>
                    <SubMenuLink
                      to={buildRoute(route, { orgId, selectedProduct, ...routeParams })}
                      onClick={() => handleSubmenuLinkClick(title, buildRoute(route, { orgId, selectedProduct, ...routeParams }))}
                    >
                      <StyledArrow name="arrowMenu" direction="right" />
                      <NavigationListItemSubtitle>{title}</NavigationListItemSubtitle>
                    </SubMenuLink>
                  </li>
                ))}
              </SubMenu>
            </AnimatedSubMenuWrapper>
          </>
        )}
      </MainTooltip>
    </li>
  );
};

export default NavigationListItem;
